import { useState } from "react";
import {
	CHECKPOINT_CYCLE,
	CHECKPOINT_LABELS,
	type MpCheckpoint,
} from "../../../common/multiplayerConfig.ts";
import type { View } from "../../../common/types.ts";
import { PHASE } from "../../../common/constants.ts";

const nextLabelKey = (checkpoint: MpCheckpoint): MpCheckpoint => {
	const i = CHECKPOINT_CYCLE.indexOf(checkpoint);
	return CHECKPOINT_CYCLE[(i + 1) % CHECKPOINT_CYCLE.length]!;
};
import { ActionButton } from "../../components/ActionButton.tsx";
import MpLiveScores from "./MpLiveScores.tsx";
import MpLivePlayByPlay from "./MpLivePlayByPlay.tsx";
import useTitleBar from "../../hooks/useTitleBar.tsx";
import { helpers } from "../../util/helpers.ts";
import { useLocal } from "../../util/local.ts";
import { logEvent } from "../../util/logEvent.ts";
import { toWorker } from "../../util/toWorker.ts";

const showError = (error: unknown) => {
	logEvent({
		type: "error",
		text: error instanceof Error ? error.message : String(error),
		saveToDb: false,
	});
};

type Offer = Extract<
	View<"multiplayerLeague">,
	{ isMultiplayer: true }
>["offers"][number];

const statusBadge = (status: Offer["status"]) => {
	const variant = {
		pending: "warning",
		accepted: "success",
		rejected: "secondary",
		invalid: "danger",
		cancelled: "secondary",
	}[status];
	return <span className={`badge text-bg-${variant}`}>{status}</span>;
};

const AssetList = ({
	assets,
}: {
	assets: Offer["payload"]["eventTeams"][number]["assets"];
}) => {
	if (assets.length === 0) {
		return <i>nothing</i>;
	}
	return (
		<>
			{assets.map((asset: any, i) => (
				<span key={i}>
					{i > 0 ? ", " : null}
					{asset.pid !== undefined ? (
						<>
							{asset.name}{" "}
							<span className="text-body-secondary">
								({helpers.formatCurrency(asset.contract.amount / 1000, "M")}/yr
								thru {asset.contract.exp})
							</span>
						</>
					) : (
						<>
							{asset.season} round {asset.round} pick
						</>
					)}
				</span>
			))}
		</>
	);
};

const OfferCard = ({
	offer,
	myTid,
	memberName,
	processing,
	onRespond,
	onCancel,
}: {
	offer: Offer;
	myTid: number | null;
	memberName: (tid: number) => string;
	processing: boolean;
	onRespond?: (accept: boolean) => void;
	onCancel?: () => void;
}) => {
	const myIndex = offer.payload.tids.indexOf(myTid ?? -99) as 0 | 1;
	const otherIndex = myIndex === 0 ? 1 : 0;
	const incoming = offer.to_tid === myTid && offer.status === "pending";

	// eventTeams[i] holds the assets RECEIVED by payload.tids[i]
	const iReceive =
		myIndex >= 0 ? offer.payload.eventTeams[myIndex]!.assets : [];
	const theyReceive =
		myIndex >= 0 ? offer.payload.eventTeams[otherIndex]!.assets : [];

	return (
		<div className="card mb-2">
			<div className="card-body">
				<div className="d-flex align-items-center gap-2 mb-2">
					<b>
						{incoming
							? `From ${memberName(offer.from_tid)}`
							: `To ${memberName(offer.to_tid)}`}
					</b>
					{statusBadge(offer.status)}
					<span className="text-body-secondary ms-auto">
						{new Date(offer.created_at).toLocaleString()}
					</span>
				</div>
				<div>
					You receive: <AssetList assets={iReceive} />
				</div>
				<div>
					You send: <AssetList assets={theyReceive} />
				</div>
				{offer.status === "pending" ? (
					<div className="d-flex gap-2 mt-2">
						{onRespond ? (
							<>
								<ActionButton
									processing={processing}
									onClick={() => onRespond(true)}
								>
									Accept
								</ActionButton>
								<ActionButton
									variant="danger"
									processing={processing}
									onClick={() => onRespond(false)}
								>
									Reject
								</ActionButton>
							</>
						) : null}
						{onCancel ? (
							<ActionButton
								variant="secondary"
								processing={processing}
								onClick={onCancel}
							>
								Cancel Offer
							</ActionButton>
						) : null}
					</div>
				) : null}
			</div>
		</div>
	);
};

type MpLeagueProps = Extract<
	View<"multiplayerLeague">,
	{ isMultiplayer: true }
>;
const PlayoffPanel = ({
	series,
	amIAlive,
	aliveCount,
	runnerName,
}: {
	series: MpLeagueProps["playoffSeries"];
	amIAlive: boolean;
	aliveCount: number;
	runnerName: string;
}) => {
	return (
		<div className="card mb-3 border-primary" style={{ maxWidth: 720 }}>
			<div className="card-body">
				<h2 className="card-title">Playoffs</h2>
				<p className="text-body-secondary">
					The bracket is set. Set your lineup, playing time, and
					play-through-injury settings on the{" "}
					<a href={helpers.leagueUrl(["roster"])}>Roster</a> page -{" "}
					<b>they lock in when you ready up</b> and the playoffs are played with
					them. Then <b>{runnerName}</b> runs the playoffs (day by day or all at
					once) through to the draft. Full bracket on the{" "}
					<a href={helpers.leagueUrl(["playoffs"])}>Playoffs</a> page.
				</p>
				{series.length === 0 ? (
					<p className="text-body-secondary mb-0">The bracket is loading...</p>
				) : (
					<ul className="list-group" style={{ maxWidth: 520 }}>
						{series.map((s, i) => {
							const decided =
								s.homeWon >= s.numGamesToWin || s.awayWon >= s.numGamesToWin;
							const involvesHuman = s.homeHuman || s.awayHuman;
							return (
								<li
									key={i}
									className={`list-group-item d-flex align-items-center gap-2 ${
										involvesHuman ? "" : "text-body-secondary"
									}`}
								>
									<span className={s.homeHuman ? "fw-bold" : undefined}>
										({s.homeSeed}) {s.homeName}
									</span>
									<span className="ms-auto">
										{s.homeWon} - {s.awayWon}
									</span>
									<span className={`ms-auto ${s.awayHuman ? "fw-bold" : ""}`}>
										({s.awaySeed}) {s.awayName}
									</span>
									{decided ? (
										<span className="badge text-bg-secondary">final</span>
									) : involvesHuman ? (
										<span className="badge text-bg-warning">live</span>
									) : null}
								</li>
							);
						})}
					</ul>
				)}
				<p className="mt-2 mb-0">
					{amIAlive ? (
						<span className="badge text-bg-success">
							Your team is in the playoffs - ready up below
						</span>
					) : (
						<span className="badge text-bg-secondary">
							Your team missed the playoffs - no ready-up needed, enjoy the show
							({aliveCount} human {aliveCount === 1 ? "team" : "teams"} in)
						</span>
					)}
				</p>
			</div>
		</div>
	);
};

const DraftPanel = ({
	active,
	onClockTid,
	pendingPid,
	myTid,
	memberName,
	isRunner,
	runnerName,
	autoDraft,
	onToggleAutoDraft,
}: {
	active: boolean;
	onClockTid: number | null;
	pendingPid: number | null;
	myTid: number | null;
	memberName: (tid: number) => string;
	isRunner: boolean;
	runnerName: string;
	autoDraft: boolean;
	onToggleAutoDraft: (value: boolean) => void;
}) => {
	const onTheClock = onClockTid !== null && onClockTid === myTid;
	const waitingForPick = pendingPid !== null;
	return (
		<div className="card mb-3 border-primary" style={{ maxWidth: 720 }}>
			<div className="card-body">
				<h2 className="card-title">Draft</h2>
				<p className="text-body-secondary">
					The draft pauses on each human team's pick. When it's your turn,
					select a player on the{" "}
					<a href={helpers.leagueUrl(["draft"])}>Draft</a> page - everyone's
					board updates automatically. AI teams pick instantly.
				</p>
				{!active ? (
					<p className="mb-0">
						<span className="badge text-bg-info">Draft not started yet</span>{" "}
						{isRunner
							? "Once everyone has readied up, click Start draft above to begin. It'll pause on your pick."
							: `Ready up below. Once everyone's ready, ${runnerName} starts the draft and it pauses on your pick.`}
					</p>
				) : waitingForPick ? (
					<p className="mb-0">
						<span className="badge text-bg-secondary">
							Recording {onClockTid !== null ? memberName(onClockTid) : "the"}{" "}
							pick...
						</span>
					</p>
				) : onTheClock ? (
					<p className="mb-0">
						<span className="badge text-bg-success">You're on the clock!</span>{" "}
						<a href={helpers.leagueUrl(["draft"])}>Go to the Draft page</a> to
						pick.
					</p>
				) : (
					<p className="mb-0">
						<span className="badge text-bg-warning">
							On the clock:{" "}
							{onClockTid !== null ? memberName(onClockTid) : "waiting..."}
						</span>
					</p>
				)}
				{active ? (
					<div className="form-check mt-2">
						<input
							className="form-check-input"
							type="checkbox"
							id="mp-auto-draft"
							checked={autoDraft}
							onChange={(event) => {
								onToggleAutoDraft(event.target.checked);
							}}
						/>
						<label className="form-check-label" htmlFor="mp-auto-draft">
							Auto-draft my picks (best available) - sim the rest of the draft
							for me
						</label>
					</div>
				) : null}
			</div>
		</div>
	);
};

const FiringsPanel = ({
	leagueId,
	pendingFirings,
	hotSeats,
	isCommissioner,
	teamName,
	memberName,
}: {
	leagueId: string;
	pendingFirings: MpLeagueProps["pendingFirings"];
	hotSeats: MpLeagueProps["hotSeats"];
	isCommissioner: boolean;
	teamName: (tid: number) => string;
	memberName: (tid: number) => string;
}) => {
	const [busy, setBusy] = useState<string | undefined>(undefined);

	if (pendingFirings.length === 0 && hotSeats.length === 0) {
		return null;
	}

	const override = async (userId: string) => {
		setBusy(userId);
		try {
			await toWorker("main", "mpOverrideFiring", { leagueId, userId });
		} catch (error) {
			showError(error);
		}
		setBusy(undefined);
	};

	return (
		<div className="card mb-3 border-warning" style={{ maxWidth: 720 }}>
			<div className="card-body">
				<h2 className="card-title">Commissioner: front office</h2>

				{pendingFirings.length > 0 ? (
					<>
						<h3 className="h6">Pending firings</h3>
						<p className="text-body-secondary">
							These GMs will be reassigned when the season is simulated.
							{isCommissioner
								? " As commissioner you can cancel any of them."
								: null}
						</p>
						<ul className="list-group mb-3" style={{ maxWidth: 640 }}>
							{pendingFirings.map((f) => (
								<li
									key={f.user_id}
									className="list-group-item d-flex align-items-center gap-2 flex-wrap"
								>
									<span>
										<b>{memberName(f.tid)}</b> - {f.reason}.{" "}
										{f.fail_upward ? "Failing upward to" : "Reassigned to"}{" "}
										<b>{teamName(f.new_tid)}</b>.
									</span>
									{isCommissioner ? (
										<button
											className="btn btn-sm btn-light-bordered ms-auto"
											disabled={busy === f.user_id}
											onClick={() => override(f.user_id)}
										>
											Keep them
										</button>
									) : null}
								</li>
							))}
						</ul>
					</>
				) : null}

				{hotSeats.length > 0 ? (
					<>
						<h3 className="h6">Hot seat</h3>
						<ul className="list-group" style={{ maxWidth: 640 }}>
							{hotSeats.map((h) => (
								<li key={h.tid} className="list-group-item">
									<b>{teamName(h.tid)}</b>: {h.message}
								</li>
							))}
						</ul>
					</>
				) : null}
			</div>
		</div>
	);
};

const MultiplayerLeague = (props: View<"multiplayerLeague">) => {
	useTitleBar({ title: "Multiplayer" });

	const { teamInfoCache } = useLocal(["teamInfoCache"]);
	const [processing, setProcessing] = useState(false);
	// All hooks must run before any early return below, or React throws
	// "Rendered fewer hooks than expected" (#300) when the view transiently
	// hits mpError during a snapshot import and then recovers.
	const [rebuilding, setRebuilding] = useState(false);
	const [simming, setSimming] = useState(false);

	if (!props.isMultiplayer) {
		return (
			<div className="alert alert-info d-inline-block">
				This is a normal single-player league, not connected to a multiplayer
				lobby.
			</div>
		);
	}

	const {
		session,
		league,
		members,
		offers,
		myTid,
		budgetUsed,
		budgetTotal,
		lastSeq,
		checksumInfo,
		mpError,
	} = props;

	if (mpError) {
		return (
			<div className="alert alert-danger d-inline-block">
				Could not reach the multiplayer server: {mpError}
			</div>
		);
	}

	const memberName = (tid: number) => {
		const member = members.find((m) => m.tid === tid);
		const teamName = teamInfoCache[tid]
			? `${teamInfoCache[tid].region} ${teamInfoCache[tid].name}`
			: `Team ${tid}`;
		return member ? `${member.display_name} (${teamName})` : teamName;
	};

	const respond = async (offerId: string, accept: boolean) => {
		setProcessing(true);
		try {
			const result = await toWorker("main", "mpRespondTradeOffer", {
				offerId,
				accept,
			});
			if (result.status === "invalid" && result.message) {
				showError(new Error(result.message));
			}
		} catch (error) {
			showError(error);
		}
		setProcessing(false);
	};

	const cancel = async (offerId: string) => {
		setProcessing(true);
		try {
			await toWorker("main", "mpCancelTradeOffer", offerId);
		} catch (error) {
			showError(error);
		}
		setProcessing(false);
	};

	const incoming = offers.filter(
		(o) => o.status === "pending" && o.to_tid === myTid,
	);
	const outgoing = offers.filter(
		(o) => o.status === "pending" && o.from_tid === myTid,
	);
	const resolved = offers.filter((o) => o.status !== "pending").slice(0, 10);

	const me = members.find((m) => m.user_id === session?.userId);
	const readyCount = members.filter((m) => m.ready).length;
	const { isRunner, isCommissioner, runnerName } = props;

	const toggleReady = async () => {
		setProcessing(true);
		try {
			const result = await toWorker("main", "mpSetReady", {
				leagueId: props.leagueId,
				ready: !me?.ready,
			});
			if (result.status === "roster_too_big") {
				showError(
					new Error(
						`Your roster has ${(result as any).count} players; the maximum is ${(result as any).max}. Trade a player away before readying up.`,
					),
				);
			} else if (result.status === "roster_too_small") {
				showError(
					new Error(
						`Your roster has only ${(result as any).count} players; you need at least ${(result as any).min} to play games. Sign free agents (on the Free Agents page) or trade for players to fill it out - the AI won't do it for you.`,
					),
				);
			} else if (result.status === "sim_open") {
				logEvent({
					type: "info",
					text: `Everyone is ready! ${runnerName} can now simulate to the next checkpoint.`,
					saveToDb: false,
					showNotification: true,
				});
			} else if (result.status === "sim_in_progress") {
				showError(
					new Error("A simulation is in progress - ready status is locked."),
				);
			}
		} catch (error) {
			showError(error);
		}
		setProcessing(false);
	};

	const rebuildFromServer = async () => {
		setRebuilding(true);
		try {
			await toWorker("main", "mpRebuildFromServer", props.leagueId);
			logEvent({
				type: "info",
				text: "Rebuilt your league from the server's canonical state.",
				saveToDb: false,
				showNotification: true,
			});
		} catch (error) {
			showError(error);
		}
		setRebuilding(false);
	};

	const simToCheckpoint = async () => {
		setSimming(true);
		try {
			const result = await toWorker("main", "mpSimToNextCheckpoint", undefined);
			if (result.status === "stopped") {
				showError(
					new Error(
						"Simulation stopped before reaching the checkpoint. You can resume with this button or the Play menu.",
					),
				);
			}
		} catch (error) {
			showError(error);
		}
		setSimming(false);
	};

	const changeRunner = async (userId: string) => {
		try {
			await toWorker("main", "mpSetSimRunner", {
				leagueId: props.leagueId,
				userId,
			});
		} catch (error) {
			showError(error);
		}
	};

	const aliveCount = league?.playoff_alive?.length ?? 0;
	const inPlayoffs = !!league?.playoffs_active;
	const aliveReady = members.filter(
		(m) => m.ready && (league?.playoff_alive ?? []).includes(m.tid ?? -1),
	).length;
	const readyDisplay = inPlayoffs
		? `${aliveReady} of ${aliveCount} playoff teams`
		: `${readyCount} of ${members.length}`;
	const simButtonLabel =
		league?.checkpoint === "playoffs"
			? "Sim playoffs (to Draft lottery)"
			: league?.checkpoint === "draft"
				? "Start draft"
				: league
					? `Sim to ${CHECKPOINT_LABELS[nextLabelKey(league.checkpoint)]}`
					: "";
	// Humans who missed the playoffs don't gate them, so hide their Ready
	// button - unless NO human made the playoffs, in which case anyone's ready
	// opens the sim (the server's alive-gate is trivially satisfied at 0 of 0)
	const canReady = !inPlayoffs || props.amIAlive || aliveCount === 0;

	return (
		<>
			<MpLivePlayByPlay />
			<MpLiveScores />

			{league?.playoffs_active ? (
				<PlayoffPanel
					series={props.playoffSeries}
					amIAlive={props.amIAlive}
					aliveCount={aliveCount}
					runnerName={runnerName}
				/>
			) : null}

			{league &&
			props.gamePhase === PHASE.FANTASY_DRAFT &&
			!league.draft_active ? (
				<div className="card mb-3 border-primary" style={{ maxWidth: 720 }}>
					<div className="card-body">
						<h2 className="card-title">Fantasy draft</h2>
						<p className="text-body-secondary">
							The league kicks off with a live fantasy draft: every player is in
							the pool, and teams pick in a snake order. When it's your turn,
							pick on the <a href={helpers.leagueUrl(["draft"])}>Draft</a> page
							- or turn on auto-draft to have your remaining picks made for you
							(best available).
						</p>
						{isRunner ? (
							<ActionButton
								processing={processing}
								onClick={async () => {
									setProcessing(true);
									try {
										const result = await toWorker(
											"main",
											"mpStartFantasyDraft",
											props.leagueId,
										);
										if (result.status === "not_fantasy_draft") {
											showError(
												new Error(
													"This league is not waiting on a fantasy draft.",
												),
											);
										} else if (result.status === "already_active") {
											showError(new Error("The draft is already running."));
										}
									} catch (error) {
										showError(error);
									}
									setProcessing(false);
								}}
							>
								Start fantasy draft
							</ActionButton>
						) : (
							<p className="mb-0">
								<span className="badge text-bg-info">
									Waiting for {runnerName} to start the draft
								</span>
							</p>
						)}
					</div>
				</div>
			) : null}

			{league && props.gamePhase === PHASE.EXPANSION_DRAFT ? (
				<div className="card mb-3 border-warning" style={{ maxWidth: 720 }}>
					<div className="card-body">
						<h2 className="card-title">Expansion draft!</h2>
						<p className="text-body-secondary">
							New teams are joining the league, and they'll draft from the
							players you leave unprotected. Choose who to protect on the{" "}
							<a href={helpers.leagueUrl(["protect_players"])}>
								Protect Players
							</a>{" "}
							page (or leave it alone to auto-protect your best players), then
							ready up here. When everyone is ready, {runnerName} continues the
							sim: the expansion teams make their picks and the season moves on.
						</p>
					</div>
				</div>
			) : null}

			{league?.draft_active || league?.checkpoint === "draft" ? (
				<DraftPanel
					active={!!league?.draft_active}
					onClockTid={league?.draft_on_clock ?? null}
					pendingPid={league?.draft_pending_pid ?? null}
					myTid={myTid}
					memberName={memberName}
					isRunner={isRunner}
					runnerName={runnerName}
					autoDraft={!!me?.auto_draft}
					onToggleAutoDraft={async (value) => {
						try {
							await toWorker("main", "mpSetAutoDraft", {
								leagueId: props.leagueId,
								value,
							});
						} catch (error) {
							showError(error);
						}
					}}
				/>
			) : null}

			{league?.checkpoint === "offseason" ? (
				<FiringsPanel
					leagueId={props.leagueId}
					pendingFirings={props.pendingFirings}
					hotSeats={props.hotSeats}
					isCommissioner={isCommissioner}
					teamName={(tid) =>
						teamInfoCache[tid]
							? `${teamInfoCache[tid].region} ${teamInfoCache[tid].name}`
							: `Team ${tid}`
					}
					memberName={memberName}
				/>
			) : null}

			{league?.checkpoint === "resign" && !league.sim_open ? (
				<div className="card mb-3 border-primary" style={{ maxWidth: 720 }}>
					<div className="card-body">
						<h2 className="card-title">Re-sign your players</h2>
						<p className="mb-0">
							The re-sign window is open. Go to the{" "}
							<a href={helpers.leagueUrl(["negotiation"])}>Re-sign Players</a>{" "}
							page to keep your expiring players (Bird rights let you go over
							the cap). Anyone you skip becomes a free agent in the auction.
							Ready up below when you're done.
						</p>
					</div>
				</div>
			) : null}

			{league?.fa_open ? (
				<div className="card mb-3 border-primary" style={{ maxWidth: 720 }}>
					<div className="card-body">
						<h2 className="card-title">
							Free agency - one round of secret bids
						</h2>
						<p className="mb-0">
							The auction is open. Place secret bids on the{" "}
							<a href={helpers.leagueUrl(["free_agents"])}>Free Agents</a> page.
							When everyone has readied up below, the bids resolve - winners
							sign, everyone else stays a free agent, and the league moves on.
						</p>
					</div>
				</div>
			) : null}

			{league ? (
				<div className="card mb-3" style={{ maxWidth: 640 }}>
					<div className="card-body">
						<div className="d-flex align-items-center gap-3 flex-wrap">
							<div>
								<span className="text-body-secondary">Checkpoint:</span>{" "}
								<b>
									{CHECKPOINT_LABELS[league.checkpoint] ?? league.checkpoint}
								</b>
							</div>
							<div>
								<span className="text-body-secondary">Your trades:</span>{" "}
								<b
									className={
										budgetUsed >= budgetTotal ? "text-danger" : undefined
									}
								>
									{budgetUsed} of {budgetTotal}
								</b>
							</div>
							<div>
								<span className="text-body-secondary">Ready:</span>{" "}
								<b>{readyDisplay}</b>
							</div>
							{!league.sim_open && canReady ? (
								<div className="ms-auto">
									<ActionButton
										variant={me?.ready ? "secondary" : "primary"}
										processing={processing}
										onClick={toggleReady}
									>
										{me?.ready ? "Not Ready" : "Ready Up"}
									</ActionButton>
								</div>
							) : null}
						</div>
						<div className="mt-2 d-flex gap-2 flex-wrap">
							{members.map((member) => (
								<span
									key={member.user_id}
									className={`badge ${member.ready ? "text-bg-success" : "text-bg-secondary"}`}
								>
									{member.display_name}
									{member.ready ? " ✓" : ""}
								</span>
							))}
						</div>

						{league.sim_open && isRunner && !league.draft_active ? (
							<div className="mt-3">
								<div className="d-flex align-items-center gap-2">
									<ActionButton
										size="lg"
										processing={simming}
										processingText="Simulating"
										onClick={simToCheckpoint}
									>
										{simButtonLabel}
									</ActionButton>
								</div>
								<p className="text-body-secondary mt-2 mb-0">
									{inPlayoffs
										? "You're the sim runner. This button sims the whole playoffs to the Draft checkpoint - or use the Play menu (day / week / round) to run the playoffs at your own pace."
										: "Everyone is ready - you're the sim runner. Use this button, or the Play menu (day / week / month) for finer control; the sim stops automatically at the checkpoint, then publishes the results for everyone."}
								</p>
							</div>
						) : null}

						{league.sim_open && !isRunner && !league.draft_active ? (
							<p className="mt-3 mb-0">
								<span
									className="spinner-border spinner-border-sm me-1"
									role="status"
								/>
								{inPlayoffs ? (
									<>
										Waiting for <b>{runnerName}</b> to run the playoffs. Results
										download automatically when they finish.
									</>
								) : (
									<>
										Everyone is ready - waiting for <b>{runnerName}</b> to
										simulate to{" "}
										{CHECKPOINT_LABELS[nextLabelKey(league.checkpoint)]}.
										Results will download automatically.
									</>
								)}
							</p>
						) : null}

						{!league.sim_open ? (
							<p className="text-body-secondary mt-2 mb-0">
								When everyone readies up, <b>{runnerName}</b> simulates the
								games to the next checkpoint. Then trade budgets reset and a new
								round of moves begins.
							</p>
						) : null}

						{isCommissioner ? (
							<div className="mt-2 d-flex align-items-center gap-2">
								<label
									className="form-label text-body-secondary small mb-0"
									htmlFor="mp-sim-runner"
								>
									Sim runner:
								</label>
								<select
									id="mp-sim-runner"
									className="form-select form-select-sm w-auto"
									value={league.sim_runner ?? league.commissioner}
									disabled={league.sim_open}
									onChange={(event) => {
										void changeRunner(event.target.value);
									}}
								>
									{members.map((member) => (
										<option key={member.user_id} value={member.user_id}>
											{member.display_name}
										</option>
									))}
								</select>
							</div>
						) : null}
					</div>
				</div>
			) : null}

			<div className="row gy-3">
				<div className="col-lg-7 col-xl-6">
					<h2>Trade offers</h2>
					<p className="text-body-secondary">
						Trades with your friends' teams are sent as offers from the{" "}
						<a href={helpers.leagueUrl(["trade"])}>Trade</a> screen. Trades with
						AI teams execute immediately, like single player.
					</p>

					<h3 className="mt-3">Incoming</h3>
					{incoming.length === 0 ? (
						<p className="text-body-secondary">No pending offers.</p>
					) : (
						incoming.map((offer) => (
							<OfferCard
								key={offer.id}
								offer={offer}
								myTid={myTid}
								memberName={memberName}
								processing={processing}
								onRespond={(accept) => respond(offer.id, accept)}
							/>
						))
					)}

					<h3 className="mt-3">Outgoing</h3>
					{outgoing.length === 0 ? (
						<p className="text-body-secondary">No pending offers.</p>
					) : (
						outgoing.map((offer) => (
							<OfferCard
								key={offer.id}
								offer={offer}
								myTid={myTid}
								memberName={memberName}
								processing={processing}
								onCancel={() => cancel(offer.id)}
							/>
						))
					)}

					{resolved.length > 0 ? (
						<>
							<h3 className="mt-3">Recent</h3>
							{resolved.map((offer) => (
								<OfferCard
									key={offer.id}
									offer={offer}
									myTid={myTid}
									memberName={memberName}
									processing={processing}
								/>
							))}
						</>
					) : null}

					{isCommissioner && props.recentTrades.length > 0 ? (
						<div className="card mt-4 border-warning">
							<div className="card-body">
								<h3 className="card-title">Undo a trade (commissioner)</h3>
								<p className="text-body-secondary">
									Trades made this checkpoint, newest first. Undo reverses an
									accidental trade and refunds both teams' trade budget. Only
									works before the next sim, and only if none of the players or
									picks have changed hands since.
								</p>
								{props.recentTrades.map((tr) => (
									<div key={tr.seq} className="border-top py-2">
										<div className="mb-1">
											<b>{tr.teamNames[0]}</b> received:{" "}
											<AssetList assets={tr.received[0].assets} />
										</div>
										<div className="mb-2">
											<b>{tr.teamNames[1]}</b> received:{" "}
											<AssetList assets={tr.received[1].assets} />
										</div>
										<ActionButton
											variant="danger"
											processing={processing}
											onClick={async () => {
												setProcessing(true);
												try {
													const result = await toWorker(
														"main",
														"mpUndoTradeAction",
														tr.seq,
													);
													if (result?.error) {
														showError(new Error(result.error));
													}
												} catch (error) {
													showError(error);
												}
												setProcessing(false);
											}}
										>
											Undo this trade
										</ActionButton>
									</div>
								))}
							</div>
						</div>
					) : null}
				</div>

				<div className="col-lg-5 col-xl-4">
					<h2>Sync status</h2>
					<table className="table w-auto">
						<tbody>
							<tr>
								<th>Signed in as</th>
								<td>{session?.email ?? "not signed in"}</td>
							</tr>
							<tr>
								<th>Events applied</th>
								<td>{lastSeq}</td>
							</tr>
							<tr>
								<th>State checksum</th>
								<td>
									<code>{checksumInfo.checksum}</code>
								</td>
							</tr>
							<tr>
								<th>Detail</th>
								<td>
									<code className="small">
										p:{checksumInfo.players}({checksumInfo.numPlayers}) d:
										{checksumInfo.picks}({checksumInfo.numPicks})
									</code>
								</td>
							</tr>
						</tbody>
					</table>
					<p className="text-body-secondary">
						The checksum covers rosters, contracts, draft picks, and team
						records. If it matches your friends' checksums, your leagues are
						identical.
					</p>
					<ActionButton
						variant="secondary"
						processing={rebuilding}
						processingText="Rebuilding"
						onClick={rebuildFromServer}
					>
						Rebuild from server
					</ActionButton>
					<p className="text-body-secondary mt-2 mb-0">
						Supabase is the source of truth; your local copy is a cache. If your
						checksum ever disagrees with everyone else's, this re-downloads the
						canonical league and replays anything since. Your manual export
						(Tools → Export League) still works too.
					</p>
				</div>
			</div>
		</>
	);
};

export default MultiplayerLeague;
